/**
 * User Identity Fingerprint (ECHO-2318 / VAPT-006)
 *
 * The additional-info store remembers which user its clinic/role selection belongs to, so a
 * different user logging in on the same browser starts from a clean selection. The login
 * identifier is often an e-mail address, so only its SHA-256 fingerprint is persisted.
 *
 * Kept free of `@/` imports so it can be unit tested with `node --test`.
 */

const FINGERPRINT_PATTERN = /^[\da-f]{64}$/;

/** Lowercase hex SHA-256 of the user identifier. */
export async function fingerprintUserId(userId: string): Promise<string> {
  const buffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(userId));
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** True when the value is a fingerprint, as opposed to a plaintext id written by older builds. */
export function isUserIdFingerprint(value: unknown): value is string {
  return typeof value === "string" && FINGERPRINT_PATTERN.test(value);
}

/**
 * How the persisted owner of the additional-info selection relates to the user logging in:
 * - `missing`: nothing recorded (selection predates owner tracking),
 * - `same`: recorded fingerprint matches (an empty value is treated as a match, as before),
 * - `legacy-same`: a plaintext id from an older build matches and should be re-written,
 * - `different`: another user owns the selection.
 */
export type StoredUserMatch = "missing" | "same" | "legacy-same" | "different";

export function matchStoredUser(
  storedUserId: string | null | undefined,
  newUserId: string | undefined,
  newFingerprint: string | undefined,
): StoredUserMatch {
  if (storedUserId === null) return "missing";
  if (!storedUserId || storedUserId === newFingerprint) return "same";
  if (storedUserId === newUserId) return "legacy-same";
  return "different";
}
